import React, { useEffect, useMemo, useRef, useState } from 'react';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/mode-java';
import 'ace-builds/src-noconflict/mode-c_cpp';
import 'ace-builds/src-noconflict/mode-php';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-monokai';
import { evaluateCode } from './api/coding';

const LANGUAGE_CATALOG = [
  { name: 'Python 3', id: 71, ace: 'python' },
  { name: 'Java', id: 62, ace: 'java' },
  { name: 'C++', id: 54, ace: 'c_cpp' },
  { name: 'PHP', id: 68, ace: 'php' },
  { name: 'JavaScript', id: 63, ace: 'javascript' },
];

const DEFAULT_SNIPPET = '// Write your code here';

const styles = {
  container: { margin: '24px 0' },
  infoCard: {
    marginBottom: 16,
    padding: '16px',
    background: '#1e1e1e',
    borderRadius: '8px',
    border: '1px solid #3e3e3e',
    color: '#e0e0e0',
  },
  signature: {
    background: '#2d2d2d',
    padding: '4px 8px',
    borderRadius: '4px',
    color: '#a5d6a7',
    marginLeft: '8px',
    fontFamily: 'monospace',
    fontSize: '14px',
  },
  select: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #3e3e3e',
    background: '#2d2d2d',
    color: '#e0e0e0',
    fontSize: '14px',
  },
  buttonRow: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
    flexWrap: 'wrap',
  },
  primaryButton: (activeColor) => ({
    padding: '10px 20px',
    background: activeColor,
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  }),
  disabledButton: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  resultCard: {
    marginTop: 24,
    padding: '16px',
    background: '#1e1e1e',
    borderRadius: '8px',
    border: '1px solid #3e3e3e',
    color: '#e0e0e0',
  },
  preBlock: {
    background: '#2d2d2d',
    padding: '12px',
    borderRadius: '4px',
    border: '1px solid #3e3e3e',
    color: '#e0e0e0',
    fontFamily: 'monospace',
    fontSize: '13px',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
  },
  tableWrapper: { marginTop: 16, overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: {
    padding: '10px',
    background: '#424242',
    color: '#4fc3f7',
    border: '1px solid #3e3e3e',
    textAlign: 'left',
  },
  td: {
    padding: '10px',
    background: '#2d2d2d',
    color: '#e0e0e0',
    border: '1px solid #3e3e3e',
    verticalAlign: 'top',
  },
  badge: (passed) => ({
    color: passed ? '#66bb6a' : '#ef5350',
    fontWeight: 'bold',
  }),
  summaryRow: { marginBottom: 12, color: '#e0e0e0' },
  summaryLabel: { color: '#4fc3f7' },
  overallRow: { marginTop: 16, fontSize: '18px', fontWeight: 'bold' },
};

const extractSupportedLanguages = (question) => {
  if (!question) {
    return [];
  }
  const sources = [
    question.supportedLanguages,
    question.metadata?.supportedLanguages,
    question.languages,
  ];
  for (const source of sources) {
    if (Array.isArray(source) && source.length) {
      return source;
    }
  }
  if (question.language_id) {
    return [{ language_id: question.language_id }];
  }
  return [];
};

const resolveLanguages = (question) => {
  const supported = extractSupportedLanguages(question);
  const mapped = supported
    .map((entry) => {
      const identifier = entry?.languageId ?? entry?.language_id ?? entry?.id;
      const base = LANGUAGE_CATALOG.find((lang) => lang.id === identifier);
      if (!base) {
        return null;
      }
      return {
        ...base,
        name: entry?.name || base.name,
      };
    })
    .filter(Boolean);
  return mapped.length ? mapped : LANGUAGE_CATALOG;
};

const buildSignature = (question, aceMode) => {
  const signature = question?.signature || question?.metadata?.signature;
  if (!signature || !Array.isArray(signature.args)) {
    return '';
  }
  const argsList = signature.args.map((arg) => arg.name).join(', ');
  if (aceMode === 'python') {
    return `def solution(${argsList})`;
  }
  if (aceMode === 'java') {
    return `public static ${signature.returnType} solution(${signature.args
      .map((arg) => `${arg.type} ${arg.name}`)
      .join(', ')})`;
  }
  if (aceMode === 'c_cpp') {
    return `${signature.returnType} solution(${signature.args
      .map((arg) => `${arg.type} ${arg.name}`)
      .join(', ')})`;
  }
  if (aceMode === 'php') {
    return `function solution(${signature.args.map((arg) => `$${arg.name}`).join(', ')})`;
  }
  if (aceMode === 'javascript') {
    return `function solution(${argsList})`;
  }
  return '';
};

const collectTestCases = (question) => {
  if (!question) {
    return [];
  }
  const sources = [
    question.testCases,
    question.test_cases,
    question.metadata?.testCases,
    question.metadata?.test_cases,
  ];
  const firstWithData = sources.find((cases) => Array.isArray(cases) && cases.length);
  if (!Array.isArray(firstWithData)) {
    return [];
  }
  return firstWithData.map((testCase, index) => ({
    id: index + 1,
    input: testCase?.input ?? testCase?.stdin ?? '',
    expected: testCase?.expectedOutput ?? testCase?.expected ?? '',
    explanation: testCase?.explanation ?? testCase?.description ?? '',
  }));
};

const renderTestCasePreview = (cases) => {
  if (!Array.isArray(cases) || !cases.length) {
    return null;
  }
  return (
    <div style={styles.resultCard}>
      <div style={{ ...styles.summaryRow, marginBottom: 8 }}>
        <strong style={styles.summaryLabel}>Sample Test Cases</strong>
      </div>
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Test #</th>
              <th style={styles.th}>Input</th>
              <th style={styles.th}>Expected Output</th>
              <th style={styles.th}>Details</th>
            </tr>
          </thead>
          <tbody>
            {cases.map((test) => (
              <tr key={test.id}>
                <td style={styles.td}>{test.id}</td>
                <td style={styles.td}>
                  <pre style={styles.preBlock}>{test.input}</pre>
                </td>
                <td style={styles.td}>
                  <pre style={styles.preBlock}>{test.expected}</pre>
                </td>
                <td style={styles.td}>{test.explanation || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const renderEvaluateResult = (data) => {
  const results = Array.isArray(data.results) ? data.results : [];
  const total = data.totalTests ?? results.length;
  const passed = data.passedTests ?? results.filter((item) => item.passed).length;
  const failed = data.failedTests ?? Math.max(0, total - passed);
  const allPassed = data.allPassed ?? passed === total;

  return (
    <div>
      <div style={styles.summaryRow}>
        <span style={styles.summaryLabel}>Total Tests:</span> {total} |{' '}
        <span style={styles.summaryLabel}>Passed:</span>{' '}
        <span style={styles.badge(true)}>{passed}</span> |{' '}
        <span style={styles.summaryLabel}>Failed:</span>{' '}
        <span style={styles.badge(false)}>{failed}</span>
      </div>
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Test #</th>
              <th style={styles.th}>Input</th>
              <th style={styles.th}>Expected</th>
              <th style={styles.th}>Your Output</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Time</th>
            </tr>
          </thead>
          <tbody>
            {results.map((test) => {
              const compileOutput = test.compileOutput ?? null;
              const output = test.stdout ?? '';
              const stderr = test.stderr ?? '';
              const hasStdout = typeof output === 'string' && output.length > 0;
              return (
                <tr key={test.testCase ?? test.test_case}>
                  <td style={styles.td}>{test.testCase ?? test.test_case}</td>
                  <td style={styles.td}>
                    <pre style={styles.preBlock}>{test.input ?? ''}</pre>
                  </td>
                  <td style={styles.td}>
                    <pre style={styles.preBlock}>{test.expected ?? ''}</pre>
                  </td>
                  <td style={styles.td}>
                    {hasStdout && <pre style={styles.preBlock}>{output}</pre>}
                    {!hasStdout && stderr && (
                      <pre style={{ ...styles.preBlock, color: '#ef5350' }}>{stderr}</pre>
                    )}
                    {!hasStdout && !stderr && compileOutput && (
                      <pre style={{ ...styles.preBlock, color: '#ff9800' }}>Compilation error</pre>
                    )}
                    {!hasStdout && !stderr && !compileOutput && (
                      <span style={{ color: '#757575' }}>No output</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.badge(Boolean(test.passed))}>
                      {test.passed ? '✓ Passed' : '✗ Failed'}
                    </span>
                  </td>
                  <td style={styles.td}>{test.time ? `${test.time}s` : 'N/A'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={styles.overallRow}>
        <strong>Overall Result:</strong>{' '}
        <span style={{ color: allPassed ? '#66bb6a' : '#ef5350' }}>
          {allPassed ? '✓ All tests passed' : '✗ Some tests failed'}
        </span>
      </div>
    </div>
  );
};

export default function CodingEditor({ question, value, onChange, onSubmit }) {
  const [languageId, setLanguageId] = useState(null);
  const [code, setCode] = useState('');
  const [resultView, setResultView] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [languages, setLanguages] = useState(LANGUAGE_CATALOG);
  const sampleTestCases = useMemo(() => collectTestCases(question), [question]);
  const initializedQuestionRef = useRef(null);

  useEffect(() => {
    if (!question) {
      initializedQuestionRef.current = null;
    }
  }, [question]);

  useEffect(() => {
    if (!question) {
      return;
    }
    const questionKey = question?.questionId || question?._id || question?.id || null;
    if (initializedQuestionRef.current === questionKey && questionKey !== null) {
      return;
    }
    initializedQuestionRef.current = questionKey;
    const resolved = resolveLanguages(question);
    const valueLanguage = value?.languageId;
    const hasValueLanguage = resolved.some((lang) => lang.id === valueLanguage);
    const initialLanguage = hasValueLanguage
      ? valueLanguage
      : resolved[0]?.id ?? LANGUAGE_CATALOG[0].id;

    const starterCode = (() => {
      if (typeof value?.code === 'string' && value.code.length) {
        return value.code;
      }
      return (
        question?.metadata?.starterCode ||
        question?.metadata?.starter_code ||
        question?.starterCode ||
        ''
      );
    })();

    setLanguages(resolved);
    setLanguageId(initialLanguage);
    setCode(starterCode);
    setResultView(null);
    if (typeof onChange === 'function') {
      const nextPayload = { code: starterCode, languageId: initialLanguage };
      if (value?.code !== nextPayload.code || value?.languageId !== nextPayload.languageId) {
        onChange(nextPayload);
      }
    }
  }, [question, onChange, value?.code, value?.languageId]);

  useEffect(() => {
    if (!value) {
      return;
    }
    if (
      typeof value.languageId === 'number' &&
      value.languageId !== languageId &&
      languages.some((lang) => lang.id === value.languageId)
    ) {
      setLanguageId(value.languageId);
    }
    if (typeof value.code === 'string' && value.code !== code) {
      setCode(value.code);
    }
  }, [value, languageId, languages, code]);

  const aceMode = useMemo(() => {
    const language = languages.find((lang) => lang.id === languageId);
    return language?.ace || 'python';
  }, [languages, languageId]);

  const handleLanguageChange = (event) => {
    const nextLanguage = Number(event.target.value);
    setLanguageId(nextLanguage);
    if (typeof onChange === 'function') {
      onChange({ code, languageId: nextLanguage });
    }
  };

  const handleCodeChange = (nextCode) => {
    setCode(nextCode);
    if (typeof onChange === 'function') {
      onChange({ code: nextCode, languageId });
    }
  };

  const handleEvaluateCode = async () => {
    if (!languageId) {
      setResultView(<span style={{ color: '#ff9800' }}>Select a language before evaluating.</span>);
      return;
    }

    if (!code || !code.trim()) {
      setResultView(<span style={{ color: '#ff9800' }}>Write code before evaluating.</span>);
      return;
    }

    setEvaluating(true);
    setResultView(<span>Evaluating test cases...</span>);
    try {
      const response = await evaluateCode({
        questionId: question?.questionId || question?._id,
        sourceCode: code,
        languageId,
      });

      if (!response.success) {
        setResultView(
          <span style={{ color: '#ef5350' }}>{response.error || 'Evaluation failed.'}</span>
        );
        return;
      }

      setResultView(renderEvaluateResult(response));
    } catch (error) {
      setResultView(<span style={{ color: '#ef5350' }}>{String(error)}</span>);
    } finally {
      setEvaluating(false);
    }
  };

  const handleSubmit = async () => {
    if (!languageId) {
      setResultView(<span style={{ color: '#ff9800' }}>Select a language before submitting.</span>);
      return;
    }

    if (!code || !code.trim()) {
      setResultView(<span style={{ color: '#ff9800' }}>Write code before submitting.</span>);
      return;
    }

    setSubmitting(true);
    try {
      if (typeof onChange === 'function') {
        onChange({ code, languageId });
      }
      if (typeof onSubmit === 'function') {
        await onSubmit({ code, languageId });
        setResultView(<span style={{ color: '#66bb6a' }}>Code submitted successfully.</span>);
      }
    } catch (error) {
      setResultView(<span style={{ color: '#ef5350' }}>{String(error)}</span>);
    } finally {
      setSubmitting(false);
    }
  };

  const disabledStyle = (flag, color) => ({
    ...styles.primaryButton(color),
    ...(flag ? styles.disabledButton : {}),
  });

  const signature = useMemo(() => buildSignature(question, aceMode), [question, aceMode]);

  return (
    <div style={styles.container}>
      <div style={styles.infoCard}>
        <div style={{ marginBottom: '8px' }}>
          <strong style={styles.summaryLabel}>Description:</strong>{' '}
          <span>{question?.description || question?.prompt}</span>
        </div>
        {signature && (
          <div>
            <strong style={styles.summaryLabel}>Function Signature:</strong>
            <code style={styles.signature}>{signature}</code>
          </div>
        )}
      </div>

      {renderTestCasePreview(sampleTestCases)}

      <div style={{ marginBottom: 12 }}>
        <label
          htmlFor="language"
          style={{ marginRight: '8px', fontWeight: 'bold', color: '#000000ff' }}
        >
          Language:
        </label>
        <select
          id="language"
          value={languageId ?? ''}
          onChange={handleLanguageChange}
          style={styles.select}
          disabled={!languages.length}
        >
          {!languages.length && <option value="">No languages available</option>}
          {languages.map((lang) => (
            <option key={lang.id} value={lang.id}>
              {lang.name}
            </option>
          ))}
        </select>
      </div>

      <AceEditor
        mode={aceMode}
        theme="monokai"
        value={code}
        onChange={handleCodeChange}
        name="coding-question-editor"
        editorProps={{ $blockScrolling: true }}
        width="100%"
        height="400px"
        fontSize={16}
        showPrintMargin={false}
        showGutter
        highlightActiveLine
        placeholder={DEFAULT_SNIPPET}
        setOptions={{
          enableBasicAutocompletion: false,
          enableLiveAutocompletion: false,
          enableSnippets: false,
          showLineNumbers: true,
          tabSize: 4,
          useSoftTabs: true,
          behavioursEnabled: true,
        }}
      />

      <div style={styles.buttonRow}>
        <button
          type="button"
          onClick={handleEvaluateCode}
          disabled={evaluating}
          style={disabledStyle(evaluating, '#4caf50')}
        >
          {evaluating ? 'Evaluating...' : 'Evaluate Tests'}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          style={disabledStyle(submitting, '#ff9800')}
        >
          {submitting ? 'Submitting...' : 'Submit Answer'}
        </button>
      </div>

      {resultView && <div style={styles.resultCard}>{resultView}</div>}
    </div>
  );
}